import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Card,
  Space,
  Tag,
  Typography,
  Descriptions,
  Button,
  Tabs,
  List,
  Pagination,
  Empty,
  Spin,
  Modal,
  Tooltip,
  Divider,
  Result,
  Select,
} from 'antd';
import { ArrowLeftOutlined, ReloadOutlined } from '@ant-design/icons';
import { adminApi } from '@/services/api';
import type { AdminUser, AdminRoleType, AdminUserStatus, AuditLogEntry } from '@/types';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

const { Text } = Typography;

const ROLE_LABELS: Record<AdminRoleType, string> = {
  super_admin: 'Super Admin',
  product_manager: 'Product Manager',
  game_designer: 'Game Designer',
  liveops_manager: 'LiveOps Manager',
  customer_support: 'Customer Support',
  analyst: 'Analyst',
};

const ROLE_COLORS: Record<AdminRoleType, string> = {
  super_admin: 'red',
  product_manager: 'blue',
  game_designer: 'purple',
  liveops_manager: 'orange',
  customer_support: 'cyan',
  analyst: 'default',
};

const STATUS_COLORS: Record<AdminUserStatus, string> = {
  active: 'green',
  inactive: 'default',
  suspended: 'orange',
};

const ACTION_COLORS: Record<string, string> = {
  login: 'blue',
  logout: 'blue',
  login_failed: 'red',
  password_change: 'orange',
  password_reset: 'orange',
  admin_create: 'green',
  admin_update: 'orange',
  admin_delete: 'red',
  admin_role_change: 'orange',
};

function humaniseAction(action: string): string {
  return action
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const AUDIT_PAGE_SIZE = 20;

const AdminShowPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [auditTab, setAuditTab] = useState<'target' | 'actor'>('target');
  const [auditPage, setAuditPage] = useState(1);
  const [auditEntries, setAuditEntries] = useState<AuditLogEntry[]>([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditActionFilter, setAuditActionFilter] = useState<string | undefined>(undefined);
  const [auditDetail, setAuditDetail] = useState<AuditLogEntry | null>(null);

  const fetchAdmin = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await adminApi.getAdminUser(id);
      setAdmin(res.data ?? null);
      setNotFound(!res.data);
    } catch (err: unknown) {
      const apiErr = err as { response?: { status?: number } };
      if (apiErr?.response?.status === 404) {
        setNotFound(true);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchAuditLogs = useCallback(async () => {
    if (!id) return;
    setAuditLoading(true);
    try {
      const params: Record<string, string | number | boolean> = {
        page: auditPage,
        limit: AUDIT_PAGE_SIZE,
      };
      if (auditTab === 'target') {
        params.targetType = 'admin';
        params.targetId = id;
      } else {
        params.adminId = id;
      }
      if (auditActionFilter) {
        params.actionType = auditActionFilter;
      }
      const res = await adminApi.queryAuditLogs(params);
      setAuditEntries(res.data?.data ?? []);
      setAuditTotal(res.data?.total ?? 0);
    } catch (err) {
      console.error(err);
      setAuditEntries([]);
      setAuditTotal(0);
    } finally {
      setAuditLoading(false);
    }
  }, [id, auditTab, auditPage, auditActionFilter]);

  useEffect(() => {
    void fetchAdmin();
  }, [fetchAdmin]);

  useEffect(() => {
    void fetchAuditLogs();
  }, [fetchAuditLogs]);

  if (notFound) {
    return (
      <Result
        status="404"
        title="Admin not found"
        subTitle="The admin user you are looking for does not exist."
        extra={
          <Link to="/admins">
            <Button type="primary">Back to admins</Button>
          </Link>
        }
      />
    );
  }

  if (loading || !admin) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  // Build the union of action types currently in the table for the filter
  // dropdown so admins can scope down without typing.
  const availableActionTypes = Array.from(new Set(auditEntries.map((e) => e.actionType))).sort();

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/admins')}>
          Back
        </Button>
      </Space>

      <Card
        style={{ marginBottom: 16 }}
        title={
          <Space>
            <span>{admin.fullName}</span>
            <Tag color={ROLE_COLORS[admin.role.type]}>{ROLE_LABELS[admin.role.type]}</Tag>
            <Tag color={STATUS_COLORS[admin.status]}>{admin.status}</Tag>
          </Space>
        }
        extra={
          <Link to="/admins">
            <Button type="primary">Edit on Admins page</Button>
          </Link>
        }
      >
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Email">{admin.email}</Descriptions.Item>
          <Descriptions.Item label="ID">
            <Text code style={{ fontSize: 12 }}>
              {admin.id}
            </Text>
          </Descriptions.Item>
          <Descriptions.Item label="Role">{admin.role.name}</Descriptions.Item>
          <Descriptions.Item label="Created">
            {dayjs(admin.createdAt).format('YYYY-MM-DD HH:mm')}
          </Descriptions.Item>
          <Descriptions.Item label="Last Login">
            {admin.lastLoginAt ? (
              <Tooltip title={dayjs(admin.lastLoginAt).format('YYYY-MM-DD HH:mm:ss')}>
                {dayjs(admin.lastLoginAt).fromNow()}
              </Tooltip>
            ) : (
              <Text type="secondary">Never</Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label="Permissions">
            <Space wrap size={2}>
              {admin.permissions.slice(0, 10).map((p) => (
                <Tag key={p} style={{ marginRight: 0, fontSize: 11 }}>
                  {p}
                </Tag>
              ))}
              {admin.permissions.length > 10 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                  +{admin.permissions.length - 10} more
                </Text>
              )}
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </Card>

      <Card
        title="Audit Log"
        extra={
          <Button icon={<ReloadOutlined />} onClick={fetchAuditLogs} size="small">
            Refresh
          </Button>
        }
      >
        <Tabs
          activeKey={auditTab}
          onChange={(k) => {
            setAuditTab(k as 'target' | 'actor');
            setAuditPage(1);
            setAuditActionFilter(undefined);
          }}
          items={[
            { key: 'target', label: 'Changes to this admin' },
            { key: 'actor', label: 'Actions by this admin' },
          ]}
        />

        <Space style={{ marginBottom: 12 }} wrap>
          <Select<string | undefined>
            placeholder="Filter by action"
            value={auditActionFilter}
            onChange={(v) => {
              setAuditActionFilter(v);
              setAuditPage(1);
            }}
            allowClear
            style={{ width: 240 }}
            options={availableActionTypes.map((a) => ({
              value: a,
              label: humaniseAction(a),
            }))}
          />
          <Text type="secondary">
            Showing {auditEntries.length} of {auditTotal}
          </Text>
        </Space>

        {auditLoading ? (
          <div style={{ textAlign: 'center', padding: 32 }}>
            <Spin />
          </div>
        ) : auditEntries.length === 0 ? (
          <Empty
            description={
              auditTab === 'target'
                ? 'No changes recorded for this admin yet'
                : 'This admin has not performed any logged actions'
            }
          />
        ) : (
          <>
            <List<AuditLogEntry>
              dataSource={auditEntries}
              size="small"
              renderItem={(entry) => (
                <List.Item
                  key={entry.id}
                  actions={[
                    <Button
                      key="details"
                      type="link"
                      size="small"
                      onClick={() => setAuditDetail(entry)}
                    >
                      View details
                    </Button>,
                  ]}
                >
                  <List.Item.Meta
                    title={
                      <Space size={8} wrap>
                        <Tag color={ACTION_COLORS[entry.actionType] ?? 'default'}>
                          {humaniseAction(entry.actionType)}
                        </Tag>
                        {!entry.success && <Tag color="red">FAILED</Tag>}
                        <Tooltip title={dayjs(entry.createdAt).format('YYYY-MM-DD HH:mm:ss')}>
                          <Text type="secondary">{dayjs(entry.createdAt).fromNow()}</Text>
                        </Tooltip>
                      </Space>
                    }
                    description={
                      <Space direction="vertical" size={0}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {auditTab === 'target' ? (
                            <>
                              by <Text strong>{entry.adminEmail ?? '(unknown)'}</Text>
                              {' • '}
                              {entry.ipAddress}
                            </>
                          ) : (
                            <>
                              target: {entry.targetType ?? '—'}
                              {entry.targetName && ` (${entry.targetName})`}
                              {' • '}
                              {entry.ipAddress}
                            </>
                          )}
                        </Text>
                        {!entry.success && entry.errorMessage && (
                          <Text type="danger" style={{ fontSize: 12 }}>
                            {entry.errorMessage}
                          </Text>
                        )}
                      </Space>
                    }
                  />
                </List.Item>
              )}
            />
            {auditTotal > AUDIT_PAGE_SIZE && (
              <div style={{ marginTop: 16, textAlign: 'right' }}>
                <Pagination
                  current={auditPage}
                  pageSize={AUDIT_PAGE_SIZE}
                  total={auditTotal}
                  onChange={setAuditPage}
                  showSizeChanger={false}
                  showTotal={(t, r) => `${r[0]}-${r[1]} of ${t}`}
                />
              </div>
            )}
          </>
        )}
      </Card>

      <Modal
        title={
          auditDetail ? (
            <Space>
              <Tag color={ACTION_COLORS[auditDetail.actionType] ?? 'default'}>
                {humaniseAction(auditDetail.actionType)}
              </Tag>
              <Text type="secondary" style={{ fontWeight: 'normal' }}>
                {dayjs(auditDetail.createdAt).format('YYYY-MM-DD HH:mm:ss')}
              </Text>
            </Space>
          ) : (
            'Audit log entry'
          )
        }
        open={!!auditDetail}
        onCancel={() => setAuditDetail(null)}
        footer={null}
        width={720}
      >
        {auditDetail && (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <Space wrap>
              <Text type="secondary">By:</Text>
              <Text strong>{auditDetail.adminEmail ?? '(unknown)'}</Text>
              <Divider type="vertical" />
              <Text type="secondary">IP:</Text>
              <Text>{auditDetail.ipAddress}</Text>
            </Space>
            {auditDetail.targetType && (
              <Space wrap>
                <Text type="secondary">Target:</Text>
                <Tag>{auditDetail.targetType}</Tag>
                {auditDetail.targetName && <Text>{auditDetail.targetName}</Text>}
                {auditDetail.targetId && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    ({auditDetail.targetId})
                  </Text>
                )}
              </Space>
            )}
            {!auditDetail.success && auditDetail.errorMessage && (
              <Text type="danger">{auditDetail.errorMessage}</Text>
            )}
            {auditDetail.reason && (
              <div>
                <Text type="secondary">Reason: </Text>
                <Text>{auditDetail.reason}</Text>
              </div>
            )}
            <div>
              <Text type="secondary">Old value</Text>
              <pre
                style={{
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 4,
                  padding: 12,
                  margin: '4px 0 0 0',
                  maxHeight: 240,
                  overflow: 'auto',
                  fontSize: 12,
                }}
              >
                {auditDetail.oldValue ? JSON.stringify(auditDetail.oldValue, null, 2) : '—'}
              </pre>
            </div>
            <div>
              <Text type="secondary">New value</Text>
              <pre
                style={{
                  background: '#fafafa',
                  border: '1px solid #f0f0f0',
                  borderRadius: 4,
                  padding: 12,
                  margin: '4px 0 0 0',
                  maxHeight: 240,
                  overflow: 'auto',
                  fontSize: 12,
                }}
              >
                {auditDetail.newValue ? JSON.stringify(auditDetail.newValue, null, 2) : '—'}
              </pre>
            </div>
            {auditDetail.metadata && (
              <div>
                <Text type="secondary">Metadata</Text>
                <pre
                  style={{
                    background: '#fafafa',
                    border: '1px solid #f0f0f0',
                    borderRadius: 4,
                    padding: 12,
                    margin: '4px 0 0 0',
                    maxHeight: 160,
                    overflow: 'auto',
                    fontSize: 12,
                  }}
                >
                  {JSON.stringify(auditDetail.metadata, null, 2)}
                </pre>
              </div>
            )}
            {auditDetail.userAgent && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                User-Agent: {auditDetail.userAgent}
              </Text>
            )}
          </Space>
        )}
      </Modal>
    </div>
  );
};

export default AdminShowPage;
